/**
 * Platform detection for enhanced UI capabilities.
 * iOS 26+ (Sept 2025) introduced Liquid Glass material system-wide.
 * Web has no direct API to render native Liquid Glass, but we can:
 *   1. Detect the platform + OS version
 *   2. Apply an enhanced CSS "faux Liquid Glass" via layered backdrop-filter + inner highlight
 * Older iOS / Android / desktop get the lighter fallback `.glass` (perf-friendly).
 */

export function detectPlatform() {
  if (typeof navigator === "undefined") return { isIOS: false, iosMajor: 0, isAndroid: false, isMac: false };
  const ua = navigator.userAgent;
  const isTouchMac = ua.includes("Mac") && typeof document !== "undefined" && "ontouchend" in document;
  const isIOS = /iPad|iPhone|iPod/.test(ua) || isTouchMac; // iPadOS 13+ reports as Mac
  const iosMatch = ua.match(/(?:iPhone OS|CPU OS|Version)\s*(\d+)[_.]?(\d+)?/i);
  const iosMajor = isIOS && iosMatch ? parseInt(iosMatch[1], 10) : 0;
  const isAndroid = /Android/i.test(ua);
  const isMac = ua.includes("Mac") && !isTouchMac;
  return { isIOS, iosMajor, isAndroid, isMac, isTouchMac };
}

/**
 * Attaches classes to <html>:
 *   - `ios` for any iOS
 *   - `ios26` for iOS 26+ (Liquid Glass era) — unlocks enhanced glass CSS
 *   - `android` / `mac` for platform-specific tweaks
 * Also verifies browser actually supports backdrop-filter — if not, downgrade.
 */
export function applyPlatformClasses() {
  if (typeof document === "undefined") return;
  const html = document.documentElement;
  const p = detectPlatform();
  if (p.isIOS) html.classList.add("ios");
  if (p.iosMajor >= 26) html.classList.add("ios26");
  if (p.isAndroid) html.classList.add("android");
  if (p.isMac) html.classList.add("mac");
  const isStandalone =
    (typeof window !== "undefined" && window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    (typeof navigator !== "undefined" && navigator.standalone === true);
  if (isStandalone) html.classList.add("standalone");

  // capability check — CSS.supports handles both prefixed + unprefixed
  const supportsBackdrop =
    typeof CSS !== "undefined" &&
    (CSS.supports("backdrop-filter", "blur(1px)") ||
      CSS.supports("-webkit-backdrop-filter", "blur(1px)"));
  if (supportsBackdrop) html.classList.add("has-backdrop");
  else html.classList.add("no-backdrop");
}
